import ReconnectingWebsocket from 'reconnecting-websocket';
import joypixels from 'emoji-toolkit';
import router from '@/router/';
import store from '@/store/';
import { Api, Util, Url, Crypto, Notifications, SessionCache, Platform, i18n } from '@/utils';

export default class Stream {
    constructor() {
        this.socket = null;
        this.open();

        this.has_disconnected = false;
        this.disconnected_timeout = null;
    }

    /**
     * Open reconnecting websocket.
     */
    open() {
        this.socket = new ReconnectingWebsocket(Url.get('websocket') + Url.getAccountParam());

        this.socket.addEventListener('open', () => {
            // Close connection on logout
            store.state.msgbus.$on('logout-btn', () => this.close());

            if (this.has_disconnected) {
                store.state.msgbus.$emit('refresh-btn');
                Util.snackbar(i18n.t('api.back'));
            }

            if (this.disconnected_timeout != null) {
                clearTimeout(this.disconnected_timeout);
                this.disconnected_timeout = null;
            }

            this.has_disconnected = false;

            const subscribe = JSON.stringify({
                "command": "subscribe",
                "identifier": JSON.stringify({
                    "channel": "NotificationsChannel"
                })
            });

            this.socket.send(subscribe);
        });

        this.socket.addEventListener('message', (e) => this.handleMessage(e));

        this.socket.addEventListener('close', (e) => {
            if (e.wasClean || e.code == 1001) // If not an error, ignore
                return;

            // if the websocket reconnects within 5 seconds, we won't show anything to the user
            // if it takes longer than that, then we will notify the user that we are
            // trying to reconnect.
            this.disconnected_timeout = setTimeout(() => {
                if (!this.has_disconnected)
                    Util.snackbar(i18n.t('api.disconnected'));

                this.has_disconnected = true;
            }, 5 * 1000);
        });
    }

    /**
     * Perminently close socket
     */
    close() {
        this.socket.close(1000, '', { keepClosed: true });
    }

    /**
     * Handle incoming socket data
     * @param e - socket event
     */
    handleMessage(e) {
        if (e.data.indexOf("ping") != -1) { // Is keep alive event
            // Store last ping to maintain data connection
            store.commit('last_ping', Date.now() / 1000 >> 0);
            return;
        }

        // Parse out JSON
        const json = JSON.parse(e.data);

        // Ignore bad messages
        if (typeof json.message == "undefined")
            return;

        const operation = json.message.operation;

        // Parse any emojis
        if (typeof json.message.content.data != "undefined")
            json.message.content.data = joypixels.toImage(
                json.message.content.data
            );


        if (operation == "added_message") {
            let message = json.message.content;
            message.message_from = message.from;
            message = Crypto.decryptMessage(message);

            this.notify(message);

            SessionCache.cacheMessage(message);
            SessionCache.updateConversation(message);

            store.state.msgbus.$emit('newMessage', message);
        } else if (operation == "removed_message") {
            let message = json.message.content;

            SessionCache.deleteMessage(message.id);

            store.state.msgbus.$emit('deletedMessage', message.id);
        } else if (operation == "read_conversation") {
            const id = json.message.content.id;

            SessionCache.readConversation(id, 'index_public_unarchived');
            SessionCache.readConversation(id, 'index_archived');

            store.state.msgbus.$emit('conversationRead', id);
        } else if (operation == "updated_conversation") {
            const conversation = Crypto.decryptConversation(json.message.content);
            conversation.conversation_id = conversation.id; // Normalize ID

            if (conversation.snippet) {
                SessionCache.updateConversationSnippet(conversation.id, conversation.snippet, 'index_public_unarchived');
                SessionCache.updateConversationSnippet(conversation.id, conversation.snippet, 'index_archived');
                SessionCache.updateConversationSnippet(conversation.id, conversation.snippet, 'index_private');
            }

            SessionCache.readConversation(conversation.id, 'index_public_unarchived', conversation.read);
            SessionCache.readConversation(conversation.id, 'index_archived', conversation.read);

            store.state.msgbus.$emit('newMessage', conversation);
        } else if (operation == "update_message_type") {
            const id = json.message.content.id;
            const message_type = json.message.content.message_type;

            SessionCache.updateMessageType(id, message_type);
            store.state.msgbus.$emit('updateMessageType-' + id, { message_type });
        } else if (operation == "added_conversation") {
            const id = json.message.content.id;

            SessionCache.invalidateConversations('index_public_unarchived');
            store.state.msgbus.$emit('addedConversation', { id });
        } else if (operation == "removed_conversation") {
            const id = json.message.content.id;

            SessionCache.removeConversation(id, 'index_public_unarchived');
            SessionCache.removeConversation(id, 'index_archived');
            SessionCache.removeConversation(id, 'index_private');
            store.state.msgbus.$emit('removedConversation', { id });
        } else if (operation == "archive_conversation") {
            const id = json.message.content.id;

            if (json.message.content.archive) {
                SessionCache.removeConversation(id, 'index_public_unarchived');
                SessionCache.invalidateConversations('index_archived');
            } else {
                SessionCache.removeConversation(id, 'index_archived');
                SessionCache.invalidateConversations('index_public_unarchived');
            }

            store.state.msgbus.$emit('removedConversation', { id });
        } else if (operation === 'added_contact') {
            let contact = json.message.content;
            // Contact doesn't have ID when sent via action cable. The device_id is also unique and we can use it too
            // as ID.
            if (!contact.id) {
                contact.id = contact.device_id;
            }
            contact.contact_type = contact.type;
            contact = Crypto.decryptContact(contact);
            SessionCache.cacheContact(contact);

            store.state.msgbus.$emit('addedContact', contact.device_id);
        } else if (operation === 'updated_contact') {
            let contact = json.message.content;
            contact.contact_type = contact.type;
            contact = Crypto.decryptContact(contact);
            SessionCache.updateContact(contact);

            store.state.msgbus.$emit('updatedContact', contact.device_id);
        } else if (operation === 'removed_contact') {
            let phoneNumbers = json.message.content.id;
            phoneNumbers = phoneNumbers.split(',');
            phoneNumbers = phoneNumbers.map(function (phoneNumber) {
                return Crypto.decrypt(phoneNumber);
            });
            SessionCache.deleteContactsByPhoneNumbers(phoneNumbers);

            store.state.msgbus.$emit('deletedContactsByPhoneNumbers', phoneNumbers);
        } else if (operation === 'removed_contact_by_id') {
            let ids = json.message.content.id;
            ids = ids.split(',');
            SessionCache.deleteContactsByIds(ids);

            store.state.msgbus.$emit('deletedContactsByIds', ids);
        } else if (operation === 'removed_auto_reply') {
            SessionCache.deleteAutoReply(json.message.content.id);
        } else if (operation === 'added_auto_reply') {
            let autoReply = Crypto.decryptAutoReply(json.message.content);
            autoReply.reply_type = autoReply.type;
            SessionCache.addAutoReply(autoReply);
        } else if (['added_template', 'updated_template', 'removed_template'].includes(operation)) {
            let template = json.message.content;
            template = Crypto.decryptTemplate(template);
            switch (operation) {
                case 'added_template':
                    SessionCache.addTemplate(template);
                    break;
                case 'updated_template':
                    SessionCache.updateTemplate(template);
                    break;
                case 'removed_template':
                    SessionCache.deleteTemplate(template.id);
                    break;
            }
        } else if (['added_scheduled_message', 'updated_scheduled_message', 'removed_scheduled_message'].includes(operation)) {
            let scheduled = json.message.content;
            scheduled = Crypto.decryptScheduledMessage(scheduled);
            // server sends device_id as id
            scheduled.device_id = scheduled.id;
            switch (operation) {
                case 'added_scheduled_message':
                    SessionCache.addScheduledMessage(scheduled);
                    break;
                case 'updated_scheduled_message':
                    SessionCache.updateScheduledMessage(scheduled);
                    break;
                case 'removed_scheduled_message':
                    SessionCache.deleteScheduledMessage(scheduled.id);
                    break;
            }
        } else if (['added_blacklist', 'removed_blacklist'].includes(operation)) {
            let blacklist = json.message.content;
            blacklist = Crypto.decryptBlacklist(blacklist);
            blacklist.device_id = blacklist.id;
            switch (operation) {
                case 'added_blacklist':
                    SessionCache.addBlacklist(blacklist);
                    break;
                case 'removed_blacklist':
                    SessionCache.deleteBlacklist(blacklist.id);
                    break;
            }
        } else if (operation === 'update_setting') {
            const pref = json.message.content.pref;
            const value = json.message.content.value;
            if (pref === 'cleanup_old_messages') {
                store.commit('cleanup_old_messages', value);
            }
            if (pref === 'unknown_number_reception') {
                store.commit('unknown_number_reception', value);
            }
        }
    }

    /**
     * Submit notification for message
     * @param message  - message object
     */
    notify(message) {
        if (Notifications.needsPermission() && !store.state.notifications)
            return;

        if (message.type != 0)
            return;

        if (!Platform.isWebsite()) {
            return;
        }

        // fetch through the API instead of the session cache, since the session
        // cache doesn't know about the mute/private settings
        Api.conversations.getById(message.conversation_id).then(conversation => {
            if (conversation == null || conversation.mute) {
                return;
            }

            const title = conversation.title;
            const snippet = conversation.private_notifications ? "" : Util.generateSnippet(message);

            const link = "/thread/" + message.conversation_id;

            const notification = Notifications.notify(title, {
                icon: require('@/../public/images/android-desktop.png'),
                body: snippet
            });

            notification.onclick = () => {
                window.focus();
                router.push(link).catch(() => {});
            };
        });
    }
}
